/**
 * Starter room and service templates seeded into every new company.
 *
 * These are only defaults — a company owner edits, deactivates or adds to them
 * from Settings. The time estimates are deliberately conservative mid-range
 * figures for a two-person residential crew.
 *
 * The arrays below are the English originals and the shape everything else is
 * checked against; `seedRoomTemplates()` / `seedServiceTemplates()` are what
 * registration actually seeds from, returning the same library in whichever
 * language the new owner signed up in. Timings, icons, categories-as-grouping
 * and the room/task wiring are language-independent and live here alone — only
 * the human-readable strings are translated, in `seed-templates.<lang>.ts`.
 */

import { DEFAULT_LANGUAGE, SUPPORTED_LANGUAGES, type SupportedLanguage } from './languages.ts';
import type { LocalizedText } from './localized-text.ts';
import { SEED_TRANSLATIONS_ES } from './seed-templates.es.ts';
import type { FloorType, MeasurementBasis } from './index.ts';
import {
  guessTaskCategory,
  guessRoomCategory,
  residentialTags,
  type TaskCategory,
  type RoomCategory,
  type TemplateTags,
} from './template-taxonomy.ts';

export interface SeedServiceTemplate {
  /** Stable key used to wire services to rooms below. Not persisted. */
  readonly key: string;
  readonly name: string;
  readonly customerDescription: string;
  readonly category: string;
  readonly defaultMinutes: number;
  readonly minimumMinutes: number;
  readonly maximumMinutes: number;
  /**
   * How this task is usually estimated. Absent means 'time' — somebody
   * decided it takes eight minutes — which is every task in the residential
   * library and the only thing a house is ever estimated by.
   */
  readonly measurementBasis?: MeasurementBasis;
  /**
   * The rate to start from when this task is measured: square feet per hour
   * under 'area', fixtures per hour under 'fixture'. A starting point the
   * estimator overrules, like every other number in this file.
   */
  readonly productionRatePerHour?: number;
  /** Picker category. Absent means a best guess from the name. */
  readonly categoryCode?: TaskCategory;
  readonly tags?: TemplateTags;
}

export interface SeedRoomTemplate {
  readonly key: string;
  readonly name: string;
  readonly description: string;
  readonly iconName: string;
  /**
   * Whether this kind of area is measured, and in what. Absent means 'time':
   * a bedroom is a bedroom, and nobody measures it.
   */
  readonly measurementBasis?: MeasurementBasis;
  /** What is usually underfoot, when this kind of area is measured. */
  readonly defaultFloorType?: FloorType;
  readonly categoryCode?: RoomCategory;
  readonly tags?: TemplateTags;
  /** How many of this room a new quote line starts with. */
  readonly defaultQuantity?: number;
  readonly defaultSquareFeet?: number;
  /** Service keys offered for this room, in display order. */
  readonly services: readonly SeedRoomService[];
}

export interface SeedRoomService {
  readonly serviceKey: string;
  readonly defaultSelected: boolean;
  readonly minutesOverride?: number;
  /**
   * The rate this task runs at in THIS kind of area, when it differs from the
   * task's own default — a mop covers twice the ground over open concrete
   * that it covers between restroom fixtures. Absent means the task's rate.
   */
  readonly productionRateOverride?: number;
  readonly measuredQuantity?: number;
}

const SEED_SERVICE_SOURCE = [
  // --- General -------------------------------------------------------------
  {
    key: 'dust_surfaces',
    name: 'Dust all surfaces',
    customerDescription: 'Dust reachable surfaces, furniture and decor.',
    category: 'General',
    defaultMinutes: 8,
    minimumMinutes: 3,
    maximumMinutes: 30,
  },
  {
    key: 'vacuum_floors',
    name: 'Vacuum floors',
    customerDescription: 'Vacuum carpet and rugs, including edges.',
    category: 'General',
    defaultMinutes: 8,
    minimumMinutes: 3,
    maximumMinutes: 40,
  },
  {
    key: 'mop_floors',
    name: 'Mop hard floors',
    customerDescription: 'Damp mop hard flooring with an appropriate cleaner.',
    category: 'General',
    defaultMinutes: 10,
    minimumMinutes: 4,
    maximumMinutes: 45,
  },
  {
    key: 'empty_trash',
    name: 'Empty trash',
    customerDescription: 'Empty waste bins and replace liners.',
    category: 'General',
    defaultMinutes: 3,
    minimumMinutes: 1,
    maximumMinutes: 15,
  },
  {
    key: 'clean_mirrors',
    name: 'Clean mirrors and glass',
    customerDescription: 'Streak-free clean of mirrors and interior glass.',
    category: 'General',
    defaultMinutes: 5,
    minimumMinutes: 2,
    maximumMinutes: 25,
  },
  {
    key: 'wipe_baseboards',
    name: 'Wipe baseboards',
    customerDescription: 'Hand-wipe baseboards and trim.',
    category: 'Detail',
    defaultMinutes: 10,
    minimumMinutes: 4,
    maximumMinutes: 40,
  },
  {
    key: 'switches_handles',
    name: 'Wipe switches and handles',
    customerDescription: 'Disinfect light switches, door handles and high-touch points.',
    category: 'General',
    defaultMinutes: 4,
    minimumMinutes: 2,
    maximumMinutes: 15,
  },
  {
    key: 'window_sills',
    name: 'Clean window sills and tracks',
    customerDescription: 'Detail window sills, ledges and tracks.',
    category: 'Detail',
    defaultMinutes: 8,
    minimumMinutes: 3,
    maximumMinutes: 30,
  },
  {
    key: 'interior_windows',
    name: 'Clean interior windows',
    customerDescription: 'Wash the inside face of windows.',
    category: 'Detail',
    defaultMinutes: 12,
    minimumMinutes: 5,
    maximumMinutes: 60,
  },
  {
    key: 'ceiling_fans_vents',
    name: 'Dust ceiling fans and vents',
    customerDescription: 'Dust fan blades, light fixtures and air vents.',
    category: 'Detail',
    defaultMinutes: 6,
    minimumMinutes: 2,
    maximumMinutes: 25,
  },
  {
    key: 'cobwebs',
    name: 'Remove cobwebs',
    customerDescription: 'Clear cobwebs from corners and ceilings.',
    category: 'General',
    defaultMinutes: 4,
    minimumMinutes: 1,
    maximumMinutes: 15,
  },
  {
    key: 'doors_frames',
    name: 'Wipe doors and frames',
    customerDescription: 'Spot-clean doors, frames and casings.',
    category: 'Detail',
    defaultMinutes: 7,
    minimumMinutes: 3,
    maximumMinutes: 25,
  },

  // --- Kitchen -------------------------------------------------------------
  {
    key: 'countertops',
    name: 'Clean and sanitize countertops',
    customerDescription: 'Clear, clean and sanitize all counter surfaces.',
    category: 'Kitchen',
    defaultMinutes: 10,
    minimumMinutes: 4,
    maximumMinutes: 35,
  },
  {
    key: 'cabinet_exteriors',
    name: 'Wipe cabinet exteriors',
    customerDescription: 'Degrease and wipe cabinet doors and fronts.',
    category: 'Kitchen',
    defaultMinutes: 12,
    minimumMinutes: 5,
    maximumMinutes: 45,
  },
  {
    key: 'cabinet_interiors',
    name: 'Clean inside cabinets',
    customerDescription: 'Empty, wipe out and restock cabinet interiors.',
    category: 'Kitchen',
    defaultMinutes: 30,
    minimumMinutes: 10,
    maximumMinutes: 120,
  },
  {
    key: 'sink_faucet',
    name: 'Scrub sink and polish faucet',
    customerDescription: 'Scrub the basin and polish fixtures.',
    category: 'Kitchen',
    defaultMinutes: 8,
    minimumMinutes: 3,
    maximumMinutes: 25,
  },
  {
    key: 'stovetop',
    name: 'Degrease stovetop and hood',
    customerDescription: 'Degrease the cooktop, grates and range hood.',
    category: 'Kitchen',
    defaultMinutes: 15,
    minimumMinutes: 5,
    maximumMinutes: 60,
  },
  {
    key: 'oven_interior',
    name: 'Clean oven interior',
    customerDescription: 'Deep clean inside the oven, including racks.',
    category: 'Kitchen',
    defaultMinutes: 35,
    minimumMinutes: 15,
    maximumMinutes: 120,
  },
  {
    key: 'microwave',
    name: 'Clean microwave',
    customerDescription: 'Clean the microwave inside and out.',
    category: 'Kitchen',
    defaultMinutes: 8,
    minimumMinutes: 3,
    maximumMinutes: 25,
  },
  {
    key: 'fridge_exterior',
    name: 'Wipe refrigerator exterior',
    customerDescription: 'Wipe down the refrigerator doors and handles.',
    category: 'Kitchen',
    defaultMinutes: 6,
    minimumMinutes: 2,
    maximumMinutes: 20,
  },
  {
    key: 'fridge_interior',
    name: 'Clean inside refrigerator',
    customerDescription: 'Empty, wash and restock refrigerator shelves and drawers.',
    category: 'Kitchen',
    defaultMinutes: 30,
    minimumMinutes: 15,
    maximumMinutes: 90,
  },
  {
    key: 'dishwasher_exterior',
    name: 'Wipe dishwasher exterior',
    customerDescription: 'Wipe the dishwasher front and controls.',
    category: 'Kitchen',
    defaultMinutes: 4,
    minimumMinutes: 1,
    maximumMinutes: 15,
  },
  {
    key: 'small_appliances',
    name: 'Wipe small appliances',
    customerDescription: 'Clean the exterior of toasters, kettles and similar.',
    category: 'Kitchen',
    defaultMinutes: 6,
    minimumMinutes: 2,
    maximumMinutes: 25,
  },
  {
    key: 'backsplash',
    name: 'Clean backsplash',
    customerDescription: 'Degrease and polish the backsplash.',
    category: 'Kitchen',
    defaultMinutes: 8,
    minimumMinutes: 3,
    maximumMinutes: 30,
  },
  {
    key: 'load_dishwasher',
    name: 'Load dishwasher',
    customerDescription: 'Load dishes left in the sink into the dishwasher.',
    category: 'Kitchen',
    defaultMinutes: 6,
    minimumMinutes: 2,
    maximumMinutes: 25,
  },

  // --- Bathroom ------------------------------------------------------------
  {
    key: 'toilet',
    name: 'Scrub and disinfect toilet',
    customerDescription: 'Disinfect the bowl, seat, tank and base.',
    category: 'Bathroom',
    defaultMinutes: 10,
    minimumMinutes: 4,
    maximumMinutes: 25,
  },
  {
    key: 'shower_tub',
    name: 'Scrub shower and tub',
    customerDescription: 'Scrub tub, tile and shower walls.',
    category: 'Bathroom',
    defaultMinutes: 20,
    minimumMinutes: 8,
    maximumMinutes: 60,
  },
  {
    key: 'shower_glass',
    name: 'Descale shower glass',
    customerDescription: 'Remove hard-water scale and soap film from glass.',
    category: 'Bathroom',
    defaultMinutes: 15,
    minimumMinutes: 5,
    maximumMinutes: 60,
  },
  {
    key: 'grout_detail',
    name: 'Detail grout lines',
    customerDescription: 'Hand-scrub tile grout.',
    category: 'Bathroom',
    defaultMinutes: 25,
    minimumMinutes: 10,
    maximumMinutes: 120,
  },
  {
    key: 'vanity',
    name: 'Clean vanity and sink',
    customerDescription: 'Clean the vanity top, sink and fixtures.',
    category: 'Bathroom',
    defaultMinutes: 8,
    minimumMinutes: 3,
    maximumMinutes: 25,
  },
  {
    key: 'restock_supplies',
    name: 'Restock paper and towels',
    customerDescription: 'Replace toilet paper and fold fresh towels.',
    category: 'Bathroom',
    defaultMinutes: 3,
    minimumMinutes: 1,
    maximumMinutes: 10,
  },

  // --- Bedroom -------------------------------------------------------------
  {
    key: 'make_beds',
    name: 'Make beds',
    customerDescription: 'Make the beds with the existing linens.',
    category: 'Bedroom',
    defaultMinutes: 5,
    minimumMinutes: 2,
    maximumMinutes: 15,
  },
  {
    key: 'change_linens',
    name: 'Change bed linens',
    customerDescription: 'Strip and remake beds with fresh linens you provide.',
    category: 'Bedroom',
    defaultMinutes: 10,
    minimumMinutes: 5,
    maximumMinutes: 25,
  },
  {
    key: 'under_bed',
    name: 'Vacuum under bed',
    customerDescription: 'Move and vacuum beneath the bed where accessible.',
    category: 'Bedroom',
    defaultMinutes: 8,
    minimumMinutes: 3,
    maximumMinutes: 25,
  },

  // --- Laundry / utility ---------------------------------------------------
  {
    key: 'washer_dryer',
    name: 'Wipe washer and dryer',
    customerDescription: 'Wipe down machine exteriors and control panels.',
    category: 'Laundry',
    defaultMinutes: 8,
    minimumMinutes: 3,
    maximumMinutes: 25,
  },
  {
    key: 'lint_trap',
    name: 'Clean lint trap',
    customerDescription: 'Clear the dryer lint trap and surround.',
    category: 'Laundry',
    defaultMinutes: 3,
    minimumMinutes: 1,
    maximumMinutes: 10,
  },
  {
    key: 'utility_sink',
    name: 'Clean utility sink',
    customerDescription: 'Scrub the utility sink and taps.',
    category: 'Laundry',
    defaultMinutes: 8,
    minimumMinutes: 3,
    maximumMinutes: 25,
  },

  // --- Circulation and utility areas ---------------------------------------
  {
    key: 'vacuum_stairs',
    name: 'Vacuum stairs',
    customerDescription: 'Vacuum treads, risers and stair edges.',
    category: 'General',
    defaultMinutes: 12,
    minimumMinutes: 5,
    maximumMinutes: 40,
  },
  {
    key: 'railings',
    name: 'Wipe railings and spindles',
    customerDescription: 'Hand-wipe handrails, spindles and newel posts.',
    category: 'Detail',
    defaultMinutes: 8,
    minimumMinutes: 3,
    maximumMinutes: 30,
  },
  {
    key: 'sweep_floor',
    name: 'Sweep floor',
    customerDescription: 'Sweep and remove loose debris.',
    category: 'General',
    defaultMinutes: 12,
    minimumMinutes: 4,
    maximumMinutes: 45,
  },
  {
    key: 'dust_shelving',
    name: 'Dust shelving',
    customerDescription: 'Dust open shelving and storage racks.',
    category: 'General',
    defaultMinutes: 10,
    minimumMinutes: 3,
    maximumMinutes: 40,
  },

  // --- Office --------------------------------------------------------------
  {
    key: 'desk_surfaces',
    name: 'Wipe desk and work surfaces',
    customerDescription: 'Clean desks and work surfaces around your belongings.',
    category: 'Office',
    defaultMinutes: 8,
    minimumMinutes: 3,
    maximumMinutes: 30,
  },
  {
    key: 'clean_blinds',
    name: 'Clean blinds',
    customerDescription: 'Dust or wipe window blinds. An estimate — change the minutes to match the room.',
    category: 'Detail',
    defaultMinutes: 12,
    minimumMinutes: 4,
    maximumMinutes: 45,
    categoryCode: 'deep',
    tags: residentialTags(['deep', 'move_out']),
  },
  {
    key: 'electronics',
    name: 'Dust electronics and screens',
    customerDescription: 'Dust monitors, keyboards and equipment.',
    category: 'Office',
    defaultMinutes: 6,
    minimumMinutes: 2,
    maximumMinutes: 25,
  },
] as const satisfies readonly SeedServiceTemplate[];

/**
 * Widened on the way out: the const assertion above exists only so the key and
 * category unions below can be derived from the data itself, and callers want
 * the plain interface (an optional `minutesOverride` that is absent from most
 * entries, not a union of 60 exact object shapes).
 */
export const SEED_SERVICE_TEMPLATES: readonly SeedServiceTemplate[] = SEED_SERVICE_SOURCE;

const GENERAL_ROOM_SERVICES = [
  { serviceKey: 'dust_surfaces', defaultSelected: true },
  { serviceKey: 'vacuum_floors', defaultSelected: true },
  { serviceKey: 'mop_floors', defaultSelected: false },
  { serviceKey: 'empty_trash', defaultSelected: true },
  { serviceKey: 'switches_handles', defaultSelected: true },
  { serviceKey: 'cobwebs', defaultSelected: false },
  { serviceKey: 'wipe_baseboards', defaultSelected: false },
  { serviceKey: 'window_sills', defaultSelected: false },
  { serviceKey: 'interior_windows', defaultSelected: false },
  { serviceKey: 'ceiling_fans_vents', defaultSelected: false },
  { serviceKey: 'doors_frames', defaultSelected: false },
] as const satisfies readonly SeedRoomService[];

const SEED_ROOM_SOURCE = [
  {
    key: 'kitchen',
    name: 'Kitchen',
    description: 'Full kitchen including appliances and counters',
    iconName: 'kitchen',
    services: [
      { serviceKey: 'countertops', defaultSelected: true },
      { serviceKey: 'sink_faucet', defaultSelected: true },
      { serviceKey: 'stovetop', defaultSelected: true },
      { serviceKey: 'microwave', defaultSelected: true },
      { serviceKey: 'cabinet_exteriors', defaultSelected: true },
      { serviceKey: 'fridge_exterior', defaultSelected: true },
      { serviceKey: 'dishwasher_exterior', defaultSelected: true },
      { serviceKey: 'small_appliances', defaultSelected: false },
      { serviceKey: 'backsplash', defaultSelected: true },
      { serviceKey: 'load_dishwasher', defaultSelected: false },
      { serviceKey: 'oven_interior', defaultSelected: false },
      { serviceKey: 'fridge_interior', defaultSelected: false },
      { serviceKey: 'cabinet_interiors', defaultSelected: false },
      ...GENERAL_ROOM_SERVICES,
    ],
  },
  {
    key: 'full_bathroom',
    name: 'Full Bathroom',
    description: 'Bathroom with a shower or tub',
    iconName: 'bath',
    services: [
      { serviceKey: 'toilet', defaultSelected: true },
      { serviceKey: 'shower_tub', defaultSelected: true },
      { serviceKey: 'vanity', defaultSelected: true },
      { serviceKey: 'clean_mirrors', defaultSelected: true },
      { serviceKey: 'restock_supplies', defaultSelected: true },
      { serviceKey: 'shower_glass', defaultSelected: false },
      { serviceKey: 'grout_detail', defaultSelected: false },
      ...GENERAL_ROOM_SERVICES,
    ],
  },
  {
    key: 'half_bathroom',
    name: 'Half Bathroom',
    description: 'Powder room — toilet and sink only',
    iconName: 'toilet',
    services: [
      { serviceKey: 'toilet', defaultSelected: true },
      { serviceKey: 'vanity', defaultSelected: true },
      { serviceKey: 'clean_mirrors', defaultSelected: true },
      { serviceKey: 'restock_supplies', defaultSelected: true },
      ...GENERAL_ROOM_SERVICES,
    ],
  },
  {
    key: 'bedroom',
    name: 'Bedroom',
    description: 'Bedroom of any size',
    iconName: 'bed',
    services: [
      { serviceKey: 'make_beds', defaultSelected: true },
      { serviceKey: 'change_linens', defaultSelected: false },
      { serviceKey: 'under_bed', defaultSelected: false },
      { serviceKey: 'clean_mirrors', defaultSelected: false },
      ...GENERAL_ROOM_SERVICES,
    ],
  },
  {
    key: 'living_room',
    name: 'Living Room',
    description: 'Living, family or great room',
    iconName: 'sofa',
    services: [...GENERAL_ROOM_SERVICES, { serviceKey: 'electronics', defaultSelected: false }],
  },
  {
    key: 'dining_room',
    name: 'Dining Room',
    description: 'Dining room or eat-in area',
    iconName: 'utensils',
    services: [...GENERAL_ROOM_SERVICES],
  },
  {
    key: 'office',
    name: 'Office',
    description: 'Home office or study',
    iconName: 'desk',
    services: [
      { serviceKey: 'desk_surfaces', defaultSelected: true },
      { serviceKey: 'electronics', defaultSelected: true },
      ...GENERAL_ROOM_SERVICES,
    ],
  },
  {
    key: 'hallway',
    name: 'Hallway',
    description: 'Hall, landing or entryway',
    iconName: 'door',
    services: [
      { serviceKey: 'dust_surfaces', defaultSelected: true },
      { serviceKey: 'vacuum_floors', defaultSelected: true },
      { serviceKey: 'mop_floors', defaultSelected: false },
      { serviceKey: 'switches_handles', defaultSelected: true },
      { serviceKey: 'doors_frames', defaultSelected: false },
      { serviceKey: 'wipe_baseboards', defaultSelected: false },
      { serviceKey: 'cobwebs', defaultSelected: false },
    ],
  },
  {
    key: 'stairway',
    name: 'Stairway',
    description: 'Staircase and landings',
    iconName: 'stairs',
    services: [
      { serviceKey: 'vacuum_stairs', defaultSelected: true },
      { serviceKey: 'railings', defaultSelected: true },
      { serviceKey: 'wipe_baseboards', defaultSelected: false },
      { serviceKey: 'cobwebs', defaultSelected: false },
    ],
  },
  {
    key: 'laundry_room',
    name: 'Laundry Room',
    description: 'Laundry or utility room',
    iconName: 'washer',
    services: [
      { serviceKey: 'washer_dryer', defaultSelected: true },
      { serviceKey: 'lint_trap', defaultSelected: true },
      { serviceKey: 'utility_sink', defaultSelected: false },
      { serviceKey: 'sweep_floor', defaultSelected: true },
      { serviceKey: 'mop_floors', defaultSelected: true },
      { serviceKey: 'dust_shelving', defaultSelected: false },
      { serviceKey: 'cobwebs', defaultSelected: false },
    ],
  },
  {
    key: 'basement',
    name: 'Basement',
    description: 'Finished or unfinished basement',
    iconName: 'basement',
    services: [
      { serviceKey: 'sweep_floor', defaultSelected: true },
      { serviceKey: 'vacuum_floors', defaultSelected: false },
      { serviceKey: 'dust_shelving', defaultSelected: true },
      { serviceKey: 'cobwebs', defaultSelected: true },
      { serviceKey: 'dust_surfaces', defaultSelected: false },
      { serviceKey: 'window_sills', defaultSelected: false },
    ],
  },
  {
    key: 'garage',
    name: 'Garage',
    description: 'Attached or detached garage',
    iconName: 'garage',
    services: [
      { serviceKey: 'sweep_floor', defaultSelected: true },
      { serviceKey: 'dust_shelving', defaultSelected: true },
      { serviceKey: 'cobwebs', defaultSelected: true },
      { serviceKey: 'window_sills', defaultSelected: false },
    ],
  },
  {
    key: 'primary_bedroom',
    name: 'Primary bedroom',
    description: 'The largest bedroom, including an ensuite when it is cleaned with the room.',
    iconName: 'bed',
    categoryCode: 'bedrooms',
    tags: residentialTags(),
    services: [
      { serviceKey: 'make_beds', defaultSelected: true },
      { serviceKey: 'dust_surfaces', defaultSelected: true },
      { serviceKey: 'vacuum_floors', defaultSelected: true },
      { serviceKey: 'mop_floors', defaultSelected: false },
      { serviceKey: 'clean_mirrors', defaultSelected: true },
      { serviceKey: 'empty_trash', defaultSelected: true },
      { serviceKey: 'clean_blinds', defaultSelected: false },
    ],
  },
  {
    key: 'closet',
    name: 'Closet',
    description: 'Reach-in or walk-in closet.',
    iconName: 'closet',
    categoryCode: 'bedrooms',
    tags: residentialTags(['standard', 'move_out']),
    services: [
      { serviceKey: 'dust_surfaces', defaultSelected: true },
      { serviceKey: 'vacuum_floors', defaultSelected: true },
      { serviceKey: 'cobwebs', defaultSelected: false },
    ],
  },
  {
    key: 'entry',
    name: 'Entry',
    description: 'Front entry, foyer or mudroom.',
    iconName: 'door',
    categoryCode: 'entries',
    tags: residentialTags(),
    services: [
      { serviceKey: 'sweep_floor', defaultSelected: true },
      { serviceKey: 'mop_floors', defaultSelected: true },
      { serviceKey: 'dust_surfaces', defaultSelected: true },
      { serviceKey: 'clean_mirrors', defaultSelected: false },
      { serviceKey: 'doors_frames', defaultSelected: true },
    ],
  },
  {
    key: 'patio',
    name: 'Patio',
    description: 'Covered patio, balcony or porch. Times are editable estimates.',
    iconName: 'patio',
    categoryCode: 'outdoor',
    tags: residentialTags(),
    services: [
      { serviceKey: 'sweep_floor', defaultSelected: true },
      { serviceKey: 'cobwebs', defaultSelected: true },
      { serviceKey: 'doors_frames', defaultSelected: false },
    ],
  },
  {
    key: 'custom_area',
    name: 'Custom Area',
    description: 'Any area not covered by another template',
    iconName: 'square',
    services: [...GENERAL_ROOM_SERVICES],
  },
] as const satisfies readonly SeedRoomTemplate[];

/** Widened on the way out, as `SEED_SERVICE_TEMPLATES` is. */
export const SEED_ROOM_TEMPLATES: readonly SeedRoomTemplate[] = SEED_ROOM_SOURCE;

// ---------------------------------------------------------------------------
// Translations
// ---------------------------------------------------------------------------

/** Every task key the starter library defines. */
export type SeedServiceKey = (typeof SEED_SERVICE_SOURCE)[number]['key'];
/** Every room key the starter library defines. */
export type SeedRoomKey = (typeof SEED_ROOM_SOURCE)[number]['key'];
/** Every grouping label the starter tasks are filed under. */
export type SeedCategory = (typeof SEED_SERVICE_SOURCE)[number]['category'];

/**
 * One language's worth of starter-library strings.
 *
 * Deliberately a total `Record` rather than a partial one: a task added to the
 * English library above is a type error in every translation file until it is
 * translated, so the alternative — a company silently seeded with a library
 * that is half Spanish and half English — cannot be shipped by accident.
 */
export interface SeedTranslations {
  readonly services: Readonly<Record<SeedServiceKey, { readonly name: string; readonly customerDescription: string }>>;
  readonly rooms: Readonly<Record<SeedRoomKey, { readonly name: string; readonly description: string }>>;
  readonly categories: Readonly<Record<SeedCategory, string>>;
}

/**
 * Translations for every language *except* the one the arrays above are
 * written in. Typing it as a total record over `Exclude<…, 'en'>` means adding
 * a language to `SUPPORTED_LANGUAGES` fails the build until its starter
 * library exists — the app cannot gain a language whose new companies would
 * fall back to English rooms.
 */
const SEED_TRANSLATIONS: Readonly<Record<Exclude<SupportedLanguage, typeof DEFAULT_LANGUAGE>, SeedTranslations>> = {
  es: SEED_TRANSLATIONS_ES,
};

function translationsFor(language: SupportedLanguage): SeedTranslations | null {
  return language === DEFAULT_LANGUAGE ? null : SEED_TRANSLATIONS[language];
}

/**
 * A starter task or room carrying its wording in EVERY language, not only the
 * one the company signed up in.
 *
 * `name` stays what it always was — the text in the signup language, written
 * into the row's own column — and the maps beside it are what a reader in
 * another language gets instead (see localized-text.ts). Both are seeded together because the
 * library is the one place where a good translation already exists: asking an
 * owner to type forty task names twice to get a bilingual crew sheet would be
 * a feature nobody switched on.
 */
export interface LocalizedSeedServiceTemplate extends SeedServiceTemplate {
  readonly nameTranslations: LocalizedText;
  readonly customerDescriptionTranslations: LocalizedText;
  /**
   * The grouping label, translated too.
   *
   * Easy to forget, and the one that looks worst when it is: the task library
   * lists "Quitar el polvo" filed under "General" and "Kitchen", which reads
   * like a half-finished translation because it is one. The label is a heading
   * a member of staff reads and never something a customer does.
   */
  readonly categoryTranslations: LocalizedText;
}

export interface LocalizedSeedRoomTemplate extends SeedRoomTemplate {
  readonly nameTranslations: LocalizedText;
  readonly descriptionTranslations: LocalizedText;
}

/**
 * Collects one field of one seed entry across every supported language.
 *
 * Driven by `SUPPORTED_LANGUAGES` rather than by the translation table, so a
 * language added to the app is picked up here the moment its starter library
 * compiles — and `SEED_TRANSLATIONS` being a total record means it cannot
 * compile until that library exists.
 */
function acrossLanguages(read: (translations: SeedTranslations | null) => string): LocalizedText {
  const collected: LocalizedText = {};
  for (const language of SUPPORTED_LANGUAGES) collected[language] = read(translationsFor(language));
  return collected;
}

/** Optional field on a `as const` seed row, which does not carry the interface's optional keys. */
export function seedField<T>(value: object, key: string): T | undefined {
  if (!Object.prototype.hasOwnProperty.call(value, key)) return undefined;
  return (value as Record<string, T>)[key];
}

/** The starter task library in `language`, plus its wording in the others. */
export function seedServiceTemplates(language: SupportedLanguage): readonly LocalizedSeedServiceTemplate[] {
  const translations = translationsFor(language);

  return SEED_SERVICE_SOURCE.map((service) => ({
    ...service,
    categoryCode: seedField<TaskCategory>(service, 'categoryCode') ?? guessTaskCategory(service.name),
    tags:
      seedField<TemplateTags>(service, 'tags') ??
      residentialTags(
        [
          'oven_interior',
          'fridge_interior',
          'cabinet_interiors',
          'grout_detail',
          'wipe_baseboards',
          'cobwebs',
          'under_bed',
        ].includes(service.key)
          ? ['deep', 'move_out']
          : ['standard'],
      ),
    name: translations?.services[service.key].name ?? service.name,
    customerDescription: translations?.services[service.key].customerDescription ?? service.customerDescription,
    category: translations?.categories[service.category] ?? service.category,
    nameTranslations: acrossLanguages((other) => other?.services[service.key].name ?? service.name),
    customerDescriptionTranslations: acrossLanguages(
      (other) => other?.services[service.key].customerDescription ?? service.customerDescription,
    ),
    categoryTranslations: acrossLanguages((other) => other?.categories[service.category] ?? service.category),
  }));
}

/**
 * The starter room library in `language`, with its task wiring unchanged and
 * its wording in the other languages alongside.
 */
export function seedRoomTemplates(language: SupportedLanguage): readonly LocalizedSeedRoomTemplate[] {
  const translations = translationsFor(language);

  return SEED_ROOM_SOURCE.map((room) => ({
    ...room,
    categoryCode: seedField<RoomCategory>(room, 'categoryCode') ?? guessRoomCategory(room.name, room.description),
    tags: seedField<TemplateTags>(room, 'tags') ?? residentialTags(),
    defaultQuantity: seedField<number>(room, 'defaultQuantity') ?? 1,
    name: translations?.rooms[room.key].name ?? room.name,
    description: translations?.rooms[room.key].description ?? room.description,
    nameTranslations: acrossLanguages((other) => other?.rooms[room.key].name ?? room.name),
    descriptionTranslations: acrossLanguages((other) => other?.rooms[room.key].description ?? room.description),
  }));
}
